import React, { useState, useMemo, useCallback } from 'react';

// Same-origin site data endpoint — no CORS because it's on the same host.
// Public-read so the browser can GET without an auth header.
// The hash gate in the UI is the only access control in the browser layer.
const API_URL = '/.herenow/data/contacts';

// SHA-256 of the owner API key, stored here so the KeyGate can verify the
// entered key without sending it over the network. The hash cannot be reversed.
const KEY_HASH = '732c519ce13954c40dde807349bcb9a7ea325de36c618e0074e69372a9927216';

async function sha256hex(text) {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('');
}

// ── helpers ────────────────────────────────────────────────────────────────

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function exportCsv(rows) {
  const cols = ['name', 'company', 'email', 'message', 'submittedat'];
  const header = cols.join(',');
  const body = rows.map(r =>
    cols.map(c => `"${String(r[c] ?? '').replace(/"/g, '""')}"`).join(',')
  ).join('\n');
  const blob = new Blob([header + '\n' + body], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `rayosense-contacts-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ── sub-components ─────────────────────────────────────────────────────────

function SortIcon({ field, current, dir }) {
  if (field !== current) return <span className="ml-1 text-gray-300">↕</span>;
  return <span className="ml-1 text-blue-600">{dir === 'asc' ? '↑' : '↓'}</span>;
}

function Th({ label, field, sort, dir, onSort }) {
  return (
    <th
      onClick={() => onSort(field)}
      className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:bg-gray-100 transition-colors"
    >
      {label}<SortIcon field={field} current={sort} dir={dir} />
    </th>
  );
}

function Badge({ n }) {
  return (
    <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
      {n}
    </span>
  );
}

// ── key entry screen ────────────────────────────────────────────────────────

function KeyGate({ onSubmit, error }) {
  const [key, setKey] = useState('');

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-2xl mb-4">
            <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Contacts Dashboard</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Enter your here.now API key to access submissions
          </p>
        </div>

        <form
          onSubmit={e => { e.preventDefault(); onSubmit(key.trim()); }}
          className="bg-white rounded-2xl shadow-lg p-8 space-y-5"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              here.now API Key
            </label>
            <input
              type="password"
              value={key}
              onChange={e => setKey(e.target.value)}
              placeholder="hn_live_••••••••••••••••"
              required
              autoFocus
              className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
            <p className="mt-1.5 text-xs text-gray-400">
              Stored in session memory only — cleared when you close the tab.
            </p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 rounded-lg transition-colors"
          >
            Load Contacts
          </button>

          <p className="text-xs text-center text-gray-400">
            Get your key at{' '}
            <a
              href="https://here.now/dashboard"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline"
            >
              here.now/dashboard
            </a>
          </p>
        </form>
      </div>
    </div>
  );
}

// ── main dashboard ──────────────────────────────────────────────────────────

export default function ContactDashboard() {
  const [apiKey, setApiKey] = useState('');
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [authError, setAuthError] = useState('');
  const [sort, setSort] = useState('submittedat');
  const [dir, setDir] = useState('desc');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchContacts = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      // Proxy route is same-origin — here.now injects the owner key server-side.
      const res = await fetch(API_URL);
      if (res.status === 401 || res.status === 403) {
        setAuthError('Session expired. Please re-enter your key.');
        setApiKey('');
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error(`API returned ${res.status}`);
      const json = await res.json();
      const rawRows = Array.isArray(json)
        ? json
        : (json.records ?? json.data ?? json.items ?? []);
      const rows = rawRows.map(r =>
        r.data
          ? { _id: r.id, _createdAt: r.createdAt, ...r.data }
          : r
      );
      setContacts(rows);
    } catch (e) {
      setFetchError(e.message);
    }
    setLoading(false);
  }, []);

  const handleKeySubmit = async (key) => {
    setAuthError('');
    const hash = await sha256hex(key);
    if (hash !== KEY_HASH) {
      setAuthError('Invalid API key.');
      return;
    }
    setApiKey(key);
    await fetchContacts();
  };

  const handleSort = (field) => {
    if (sort === field) setDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSort(field); setDir('asc'); }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return contacts.filter(c =>
      !q || ['name', 'email', 'company', 'message'].some(
        f => String(c[f] ?? '').toLowerCase().includes(q)
      )
    );
  }, [contacts, search]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = String(a[sort] ?? '');
      const bv = String(b[sort] ?? '');
      return dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [filtered, sort, dir]);

  if (!apiKey) {
    return <KeyGate onSubmit={handleKeySubmit} error={authError} />;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-blue-600 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 leading-none">
                Contact Submissions
                <Badge n={contacts.length} />
              </h1>
              <p className="text-xs text-gray-400 mt-0.5">rayosense · here.now secure store</p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="search"
              placeholder="Search name, company, email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-56"
            />
            <button
              onClick={() => fetchContacts()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors"
            >
              <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
            <button
              onClick={() => exportCsv(sorted)}
              disabled={sorted.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-40 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Export CSV
            </button>
            <button
              onClick={() => { setApiKey(''); setContacts([]); setFetchError(''); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-red-600 border border-gray-300 rounded-lg hover:border-red-300 transition-colors"
              title="Clear session and re-enter key"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Sign out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

        {/* Error banner */}
        {fetchError && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-red-800">Error loading contacts</p>
                <pre className="mt-1 text-xs text-red-700 whitespace-pre-wrap font-mono">{fetchError}</pre>
              </div>
            </div>
          </div>
        )}

        {/* Stats row */}
        {contacts.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            {[
              { label: 'Total', value: contacts.length, color: 'blue' },
              { label: 'This month', value: contacts.filter(c => {
                const d = new Date(c.submittedat ?? c._createdAt ?? 0);
                const now = new Date();
                return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
              }).length, color: 'green' },
              { label: 'Unique companies', value: new Set(contacts.map(c => c.company).filter(Boolean)).size, color: 'purple' },
              { label: 'Unique emails', value: new Set(contacts.map(c => c.email).filter(Boolean)).size, color: 'orange' },
            ].map(s => (
              <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wide">{s.label}</p>
                <p className={`text-3xl font-bold mt-1 text-${s.color}-600`}>{s.value}</p>
              </div>
            ))}
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="bg-white rounded-xl border border-gray-200 flex items-center justify-center py-24">
            <div className="text-center">
              <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-sm text-gray-500">Loading contacts…</p>
            </div>
          </div>
        ) : sorted.length === 0 && !fetchError ? (
          <div className="bg-white rounded-xl border border-gray-200 flex items-center justify-center py-24">
            <div className="text-center">
              <svg className="w-12 h-12 text-gray-300 mx-auto mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p className="text-gray-500 font-medium">
                {search ? 'No results match your search' : 'No submissions yet'}
              </p>
              {search && (
                <button onClick={() => setSearch('')} className="mt-2 text-sm text-blue-500 hover:underline">
                  Clear search
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            {/* filter summary */}
            {search && (
              <div className="px-4 py-2 bg-blue-50 border-b border-blue-100 text-xs text-blue-700 flex items-center justify-between">
                <span>Showing {sorted.length} of {contacts.length} entries for "{search}"</span>
                <button onClick={() => setSearch('')} className="hover:underline">Clear</button>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <Th label="Name"       field="name"        sort={sort} dir={dir} onSort={handleSort} />
                    <Th label="Company"    field="company"     sort={sort} dir={dir} onSort={handleSort} />
                    <Th label="Email"      field="email"       sort={sort} dir={dir} onSort={handleSort} />
                    <Th label="Submitted"  field="submittedat" sort={sort} dir={dir} onSort={handleSort} />
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {sorted.map((c, i) => {
                    const id = c._id ?? c.id ?? i;
                    const open = expanded === id;
                    const ts = c.submittedat ?? c._createdAt ?? '';
                    return (
                      <React.Fragment key={id}>
                        <tr
                          className={`hover:bg-gray-50 cursor-pointer transition-colors ${open ? 'bg-blue-50' : ''}`}
                          onClick={() => setExpanded(open ? null : id)}
                        >
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 font-semibold text-sm flex-shrink-0">
                                {(c.name ?? '?')[0].toUpperCase()}
                              </div>
                              <span className="text-sm font-medium text-gray-900">{c.name ?? '—'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{c.company ?? '—'}</td>
                          <td className="px-4 py-3">
                            <a
                              href={`mailto:${c.email}`}
                              onClick={e => e.stopPropagation()}
                              className="text-sm text-blue-600 hover:underline"
                            >
                              {c.email ?? '—'}
                            </a>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                            {formatDate(ts)}
                          </td>
                          <td className="px-4 py-3 text-gray-400 text-right pr-4">
                            <svg
                              className={`w-4 h-4 transition-transform inline-block ${open ? 'rotate-180' : ''}`}
                              fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </td>
                        </tr>

                        {open && (
                          <tr className="bg-blue-50">
                            <td colSpan={5} className="px-4 py-4">
                              <div className="ml-11 max-w-2xl">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Message</p>
                                <p className="text-sm text-gray-800 whitespace-pre-wrap bg-white rounded-lg border border-blue-100 px-4 py-3">
                                  {c.message || '(no message)'}
                                </p>
                                <div className="mt-3 flex gap-3">
                                  <a
                                    href={`mailto:${c.email}?subject=Re: Your enquiry to Rayosense`}
                                    className="inline-flex items-center gap-1.5 text-xs bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                    </svg>
                                    Reply via email
                                  </a>
                                  <button
                                    onClick={() => navigator.clipboard.writeText(c.email ?? '')}
                                    className="inline-flex items-center gap-1.5 text-xs text-gray-600 border border-gray-300 px-3 py-1.5 rounded-lg hover:bg-white transition-colors"
                                  >
                                    Copy email
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Footer */}
            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between text-xs text-gray-400">
              <span>{sorted.length} entr{sorted.length === 1 ? 'y' : 'ies'}</span>
              <span>Data stored securely on here.now · owner-read-only</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
